import type { Tone } from "@/lib/map/layers";

/** How strongly the marks show over the fill. Low enough that a map reads as flat colour. */
const MARK_OPACITY = 0.18;

/**
 * The fill for one map level: flat colour, plus a quiet texture of hairlines so the
 * levels stay apart for a reader who cannot tell the colours apart.
 *
 * The texture is ordered like the colours: nothing on the lowest level, then one hairline,
 * then twice as many, then a crosshatch, so "more lines" always means "more reported".
 * "Limited public data" gets short dashes, the mapmaker's mark for no data. Both maps and
 * every legend swatch draw from this one definition, so a key always matches its map.
 *
 * `size` and `line` are in the host SVG's user units, so the interactive map can divide
 * them by its zoom and keep the texture the same size on screen.
 */
export function TonePattern({
  id,
  tone,
  fill,
  ink,
  size,
  line,
}: {
  id: string;
  tone: Tone;
  fill: string;
  ink: string;
  size: number;
  line: number;
}) {
  const s = size;
  return (
    <pattern id={id} width={s} height={s} patternUnits="userSpaceOnUse">
      <rect width={s} height={s} fill={fill} />
      <g stroke={ink} strokeWidth={line} strokeLinecap="square" opacity={MARK_OPACITY} fill="none">
        {tone === 1 && <path d={`M0,${s} L${s},0`} />}
        {tone === 2 && (
          <path
            d={`M0,${s} L${s},0 M${-s / 2},${s / 2} L${s / 2},${-s / 2} M${s / 2},${s * 1.5} L${s * 1.5},${s / 2}`}
          />
        )}
        {tone === 3 && <path d={`M0,${s} L${s},0 M0,0 L${s},${s}`} />}
        {tone === "none" && <path d={`M${s * 0.2},${s / 2} L${s * 0.7},${s / 2}`} />}
      </g>
    </pattern>
  );
}
